import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { ApiResponse, extractItemData } from '../../models/auth/auth-session.model';
import { AuthService } from '../auth/auth.service';
import {
  BillingInvoiceDetail,
  BillingInvoiceSummary,
  BillingOrderDetail,
  BillingOrderSummary,
  OrdersListFilters,
  CreateOrderRequest,
  CreateOrderResponse,
  PaymentGatewayRequest,
  PaymentGatewayResponse
} from '../../models/billing/billing.model';
import { collectPaginatedData } from '../shared/paginated-request.util';

@Injectable({
  providedIn: 'root'
})
export class BillingService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(private http: HttpClient, private authService: AuthService) {}

  listOrders(filters: OrdersListFilters = {}, limit = 100): Observable<BillingOrderSummary[]> {
    return this.executeWithHeaders(headers =>
      collectPaginatedData<BillingOrderSummary>(cursor =>
        this.http
          .get<ApiResponse<unknown>>(`${this.baseUrl}/billing/orders`, {
            headers,
            params: this.buildOrdersParams(limit, cursor, filters)
          })
          .pipe(catchError(err => this.handleError(err)))
      )
    );
  }

  getOrderById(orderId: string): Observable<BillingOrderDetail> {
    return this.executeWithHeaders(headers =>
      this.http.get<ApiResponse<unknown>>(`${this.baseUrl}/billing/orders/${orderId}`, { headers }).pipe(
        map(res => extractItemData<BillingOrderDetail>(res)),
        catchError(err => this.handleError(err))
      )
    );
  }

  listInvoices(limit = 100): Observable<BillingInvoiceSummary[]> {
    return this.executeWithHeaders(headers =>
      collectPaginatedData<BillingInvoiceSummary>(cursor =>
        this.http
          .get<ApiResponse<unknown>>(`${this.baseUrl}/billing/invoices`, {
            headers,
            params: this.buildCursorParams(limit, cursor)
          })
          .pipe(catchError(err => this.handleError(err)))
      )
    );
  }

  getInvoiceById(invoiceId: string): Observable<BillingInvoiceDetail> {
    return this.executeWithHeaders(headers =>
      this.http
        .get<ApiResponse<unknown>>(`${this.baseUrl}/billing/invoices/${invoiceId}`, { headers })
        .pipe(
          map(res => extractItemData<BillingInvoiceDetail>(res)),
          catchError(err => this.handleError(err))
        )
    );
  }

  createOrder(request: CreateOrderRequest): Observable<CreateOrderResponse> {
    return this.executeWithHeaders(headers =>
      this.http
        .post<ApiResponse<CreateOrderResponse>>(`${this.baseUrl}/billing/orders`, request, { headers })
        .pipe(
          map(res => res.data),
          catchError(err => this.handleError(err))
        )
    );
  }

  requestPayment(request: PaymentGatewayRequest): Observable<PaymentGatewayResponse> {
    return this.executeWithHeaders(headers =>
      this.http
        .post<ApiResponse<PaymentGatewayResponse>>(`${this.baseUrl}/payment-gateway/request`, request, { headers })
        .pipe(
          map(res => res.data),
          catchError(err => this.handleError(err))
        )
    );
  }

  private executeWithHeaders<T>(fn: (headers: HttpHeaders) => Observable<T>): Observable<T> {
    try {
      const headers = this.buildHeaders();
      return fn(headers);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo establecer la sesión.';
      return throwError(() => new Error(message));
    }
  }

  private buildHeaders(): HttpHeaders {
    const token = this.authService.getAccessToken();
    if (!token) {
      throw new Error('No existe una sesión activa para ejecutar esta operación.');
    }
    return new HttpHeaders()
      .set('X-Auth-Signly', `Bearer ${token}`)
      .set('Content-Type', 'application/json');
  }

  private buildCursorParams(limit: number, cursor?: string): HttpParams {
    let params = new HttpParams();
    if (limit) {
      params = params.set('limit', String(limit));
    }
    if (cursor) {
      params = params.set('cursor', cursor);
    }
    return params;
  }

  private buildOrdersParams(limit: number, cursor: string | undefined, filters: OrdersListFilters): HttpParams {
    let params = this.buildCursorParams(limit, cursor);
    const search = filters.search?.trim();
    if (filters.orderType) {
      params = params.set('orderType', filters.orderType);
    }
    if (filters.status) {
      params = params.set('status', filters.status);
    }
    if (search) {
      params = params.set('search', search);
    }
    if (filters.sort) {
      params = params.set('sort', filters.sort);
    }
    return params;
  }

  private handleError(error: any) {
    const message = error?.error?.message || error?.message || 'Ocurrió un error al procesar la solicitud.';
    return throwError(() => new Error(message));
  }
}
